// ANGULAR CORE MODULES
import { Injectable } from "@angular/core";
import { appSetting } from "config/appSettings";
import { AuthService } from "./auth.service";

@Injectable()
export class EreaderEventService {

    // VARIABLES

    user;
    CONSTANTS = {
        USER_ACTION_PERFORMED: 'UserActionPerformed'
    }

    /**
     * Configuration object for the timer. Used to force sync web ereader events
     */
    timerConfig = {
        /**
         * The delay in milliseconds for the timer.
         */
        timerDelay: 1000 * 60, // 1 minute

        /**
         * Indicates whether the timer is active or not.
         */
        isTimerActive: false,

        /**
         * Reference to the timer.
         */
        timerReference: undefined
    }

    /**
     * The cavClient object represents the client for the CAV (Cavalier lib).
     * It provides functionality for interacting with the CAV lib.
     */
    cavClient;

    constructor(
        private readonly authService: AuthService,
    ) {
        this.user = this.authService.getUserData();
    }

    // PUBLIC METHODS

    /**
     * Sets the cavClient property.
     * 
     * @param cavClient - The cavClient to set.
     */
    setCavClient(cavClient) {
        this.cavClient = cavClient;
    }


    /**
     * Publishes an event and saves it in local storage before syncing with the server.
     * @param data - The event data to be published.
     */
    publishEvent(data) {
        // Fetch events data from local storage
        const events: any = JSON.parse(localStorage.getItem(appSetting.localStorage.activityEvents(this.user.extUserId)) || "[]");

        // Add the new event data to the events array
        events.push(data);

        // Save the updated events array back to local storage
        localStorage.setItem(appSetting.localStorage.activityEvents(this.user.extUserId), JSON.stringify(events));

        // Trigger the sync process
        this.sync();
    }

    /**
     * Synchronizes the events data with the server.
     * @param forceSync - A boolean indicating whether to force sync the data.
     */
    sync({ forceSync = false } = {}) {
        // Fetch events data from local storage
        const events = JSON.parse(localStorage.getItem(appSetting.localStorage.activityEvents(this.user.extUserId)) || "[]");

        // Force sync data if more than 5 events or forceSync
        if (events?.length >= 5 || forceSync) {
            this._resetTimer();
            return;
        }

        // if timer is running, then return.
        if (this.timerConfig.isTimerActive) {
            return;
        }

        // Activate timer and settimeout
        this.timerConfig.isTimerActive = true;
        this.timerConfig.timerReference = setTimeout(() => {
            this._resetTimer();
        }, this.timerConfig.timerDelay);
    }

    // PRIVATE METHODS

    /**
     * Resets the timer and synchronizes the events data.
     */
    _resetTimer() {
        // reset timer
        this.timerConfig.isTimerActive = false;
        clearTimeout(this.timerConfig.timerReference);
        this.timerConfig.timerReference = undefined;

        this._syncEventsData();
    }

    /**
     * Synchronizes events data with the Cavalier service.
     * Removes events from local storage that have been successfully synced.
     */
    async _syncEventsData() {
        try {
            const events = JSON.parse(localStorage.getItem(appSetting.localStorage.activityEvents(this.user.extUserId)) || "[]");

            // If no events are present, then return
            if (!events.length) {
                return;
            }

            const eReaderEvents = [];
            const userActionEvents = events.filter((event) => {
                if(event.fact_name === this.CONSTANTS.USER_ACTION_PERFORMED) {
                    return true;
                } else {
                    eReaderEvents.push(event);
                    return false;
                }
            });

            // syncing events data with cavalier
            const [eReaderEventsCavResponse, userActionEventsCavResponse] = await Promise.all([
                eReaderEvents.length ? this.cavClient.captureEreaderEvents(eReaderEvents): Promise.resolve(false),
                userActionEvents.length ? this.cavClient.captureUserActionPerformedEvents(userActionEvents): Promise.resolve(false)
            ]);

            // If the response is successful, handle the synced events
            if (eReaderEventsCavResponse) {
                this._handleAfterEventSync(eReaderEvents);
            }

            if (userActionEventsCavResponse) {
                this._handleAfterEventSync(userActionEvents);
            }
        } catch (error) {
            console.log("Error while syncing events", error);
        }
    }

    /**
     * Handles the synchronization of events after they have been synced with the server.
     * @param syncedEvents - The events that have been successfully synced.
     */
    _handleAfterEventSync(syncedEvents) {
        // Fetch events data from local storage
        const events = JSON.parse(localStorage.getItem(appSetting.localStorage.activityEvents(this.user.extUserId)) || "[]");

        // Remove synced events from local storage
        const newEvents = events.filter((event) => {
            return !syncedEvents.some((syncedEvent) => syncedEvent.fact_id === event.fact_id);
        });

        // Save the updated events array back to local storage
        localStorage.setItem(appSetting.localStorage.activityEvents(this.user.extUserId), JSON.stringify(newEvents));
    }
}
